import React, { useMemo } from 'react'
import { navigate } from 'gatsby'
import { useLocation } from '@gatsbyjs/reach-router'
import { Button } from '../../../ui'

interface PaginatedListProps<T extends { slug: string }> {
  items: T[]
  activeItem?: T
  itemsPerPage?: number
  // eslint-disable-next-line no-unused-vars
  renderItem: (item: T, index: number) => React.ReactNode
  idElementScrollTo?: string
  totalItems?: number
}

export const PaginatedList = <T extends { slug: string }>({
  items = [],
  itemsPerPage = 24,
  renderItem,
  activeItem = undefined,
  idElementScrollTo,
  totalItems = 0,
}: PaginatedListProps<T>) => {
  const { search } = useLocation()
  const searchParams = new URLSearchParams(search)
  const pageParam = searchParams.get('page')
  const currentPage = pageParam ? parseInt(pageParam, 10) : 1
  const totalPages = Math.ceil(totalItems / itemsPerPage)

  const paginatedItems = useMemo(() => {
    let newList = items
    if (activeItem) {
      const activeItemIndex = newList.findIndex(
        (item) => item.slug === activeItem.slug,
      )
      if (activeItemIndex === -1) {
        newList = [activeItem, ...newList]
        newList.pop()
      }
    }
    return newList
  }, [currentPage, items, itemsPerPage, activeItem])

  const handlePageChange = (type: 'plus' | 'minus') => {
    const newPage = type === 'plus' ? currentPage + 1 : currentPage - 1
    if (newPage >= 1 && newPage <= totalPages) {
      searchParams.set('page', newPage.toString())
      navigate(`?${searchParams.toString()}`)
    }

    const elId = idElementScrollTo || activeItem?.slug
    if (elId) {
      const offerListElement = document.getElementById(elId)
      if (offerListElement) {
        offerListElement.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const itemsDisplayedSoFar = useMemo(() => (currentPage - 1) * itemsPerPage, [currentPage, itemsPerPage])

  return (
    <div className="relative mx-auto space-y-4 lg:space-y-8">
      <ol className="space-y-4">
        {itemsDisplayedSoFar > 0 && (
          <li className="flex items-center justify-center">
            <Button
              label={`Voir les précédents (${itemsDisplayedSoFar})`}
              color="primary"
              onClick={() => handlePageChange('minus')}
            />
          </li>
        )}

        {paginatedItems.map((item, index) => (
          <li
            key={index}
            id={item.slug}
          >
            {renderItem(item, index)}
          </li>
        ))}

        {currentPage < totalPages && (
          <li className="flex items-center justify-center">
            <Button
              label="Voir plus"
              color="primary"
              onClick={() => handlePageChange('plus')}
            />
          </li>
        )}
      </ol>
    </div>
  )
}
